import React, { useCallback, useEffect, useState } from "react";
import { Empty, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { Wrapper } from "./styles";
import { getAPICustom, getAPI } from "../../utils/api";
import { APP_NAME, VALIDATE_TOKEN_API_URL, GET_SUPPORTED_ROAD_ROUTES, GET_AIRPORTS_API_URL } from "../../utils/constants";
import { StyledTitle } from "../../components/CustomAntdComponents/Typography";
import { EncryptionHelper } from "../../utils/encryption";
import {
  TokenPayload,
  sanitizeCredentials,
  processSearchPayload,
  processSSOUser,
  resolveB2cHostUrl,
} from "./helper";
import { captureHostUrl, isEmbedded, redirectHome } from "../../utils/navigation";
import { useStore } from "../../store/global/appStore";

function Index(): React.ReactElement {
  const navigate = useNavigate();
  const { setData } = useStore();
  const { MerchantCode: paramCode, MerchantHash: paramHash } = useParams();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const encryptedDataRaw = urlSearchParams.get("Customer");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const encryptedData = encryptedDataRaw
    ?.replace(/ /g, "+")        // space → +
    ?.replace(/\%2B/gi, "+");

  useEffect(() => {
    captureHostUrl();
  }, []);

  useEffect(() => {
    const getCustomerData = async () => {
      if (encryptedData && paramCode && paramHash) {
        try {
          const decryptedData = await EncryptionHelper.decrypt(encryptedData, paramHash, paramCode);
          const parsedData = JSON.parse(decryptedData);
          localStorage.setItem(
            "trips_user",
            JSON.stringify(parsedData)
          );
        } catch (decryptionError) {
          console.error("Error decrypting customer data:", decryptionError);
        }
      }
    };
    getCustomerData();
  }, [encryptedData, paramCode, paramHash]);

  const validateUser = useCallback(async ({ MerchantCode, MerchantHash }: TokenPayload) => {
    const networErrorMsg = "Network error. Please check your connection and try again.";
    setLoading(true);
    setError("");
    try {
      const response: any = await getAPICustom(
        VALIDATE_TOKEN_API_URL,
        {},
        {
          MerchantCode,
          MerchantHash,
          AppName: APP_NAME,
        }
      );
      const apiResponse = response?.data;

      const decryptedApi = await EncryptionHelper.decrypt(apiResponse, MerchantHash, MerchantCode);
      const validateApi = JSON.parse(decryptedApi);

      if (response.status === 200) {
        const prevAuthStr = localStorage.getItem("trips_auth");
        const nextAuthStr = JSON.stringify(validateApi);
        localStorage.setItem("trips_auth", nextAuthStr);

        let needsReload = !prevAuthStr;
        if (prevAuthStr) {
          try {
            const prevObj = JSON.parse(prevAuthStr);
            if (
              prevObj?.MerchantCode !== validateApi?.MerchantCode || 
              prevObj?.ExtraData?.CurrencyCode !== validateApi?.ExtraData?.CurrencyCode ||
              prevObj?.ExtraData?.PrimaryColor !== validateApi?.ExtraData?.PrimaryColor ||
              prevObj?.ExtraData?.LightPrimaryColor !== validateApi?.ExtraData?.LightPrimaryColor ||
              prevObj?.ExtraData?.FontSize !== validateApi?.ExtraData?.FontSize
            ) {
              needsReload = true;
            }
          } catch {
            needsReload = false;
          }
        }

        const urlParams = new URLSearchParams(window.location.search);
        const searchPayload = urlParams.get("searchPayload");
        const userParamRaw = urlParams.get("user");

        if (userParamRaw) {
          processSSOUser(userParamRaw);
        }

        if (needsReload) {
          localStorage.removeItem("roadRoutes");
          localStorage.removeItem("airports");
          window.location.reload();
          return;
        }

        const airVisible = validateApi?.ExtraData?.TransportMode?.find(
          (item: any) => item.TransportModeName === 'Air'
        )?.Status;

        if (airVisible !== false) {
          try {
            const storedAirports = localStorage.getItem("airports");
            if (!storedAirports) {
              const airportsApi = await getAPI(GET_AIRPORTS_API_URL);
              if (airportsApi.status === 200 && Array.isArray(airportsApi.data)) {
                setData(airportsApi.data, 'airports');
                localStorage.setItem('airports', JSON.stringify(airportsApi.data));
              }
            } else {
              setData(JSON.parse(storedAirports), 'airports');
            }
          } catch (e) {
            console.error("Error pre-fetching airports during validation:", e);
          }
        }

        const roadVisible = validateApi?.ExtraData?.TransportMode?.find(
          (item: any) => item.TransportModeName === 'Road'
        )?.Status;

        if (roadVisible) {
          try {
            const roadRoutesAPI = await getAPI(GET_SUPPORTED_ROAD_ROUTES);
            if (roadRoutesAPI.status === 200 && roadRoutesAPI.data?.Data) {
              setData(roadRoutesAPI.data.Data, 'roadRoutes');
              localStorage.setItem('roadRoutes', JSON.stringify(roadRoutesAPI.data.Data));
            }
          } catch (e) {
            console.error("Error pre-fetching road routes during validation:", e);
          }
        }

        if (searchPayload) {
          const handled = await processSearchPayload(searchPayload, navigate, setData);
          if (handled) return;
        }

        if (isEmbedded()) {
          redirectHome();
          return;
        }

        navigate("/home");
        return;
      }

      setLoading(false);
      switch (validateApi.status) {
        case 401:
        case 400:
        case 403:
          setError(validateApi.data || "Authentication failed. Please check your credentials and try again.");
          break;

        default:
          setError(validateApi.data || networErrorMsg);
      }
    } catch (err: any) {
      setLoading(false);
      console.error("Network/API error:", err);
      if (
        err.name === "NetworkError" ||
        err.message?.includes("fetch") ||
        err.message?.includes("network") ||
        err.message?.includes("Failed to fetch") ||
        err.code === "NETWORK_ERROR"
      ) {
        setError(networErrorMsg);
        return;
      }

      if (err.name === "TimeoutError" || err.message?.includes("timeout") || err.code === "TIMEOUT") {
        setError("Connection timed out. Please check your internet connection and try again.");
        return;
      }
      setError(networErrorMsg);
    }
  }, [navigate]);

  useEffect(() => {
    try {
      // options 1: URL path parameters //https://widget.vgg.ng/QWxhZGRpbjpvcGVuc2VzYW1l
      if (paramCode && paramHash) {
        const sanitized = sanitizeCredentials(paramCode, paramHash);
        if (sanitized) {
          validateUser(sanitized);
          return;
        }
      }


      const queryCode = urlSearchParams.get("MerchantCode");
      const queryHash = urlSearchParams.get("MerchantHash");


      // options 2: URL query parameters //https://widget.vgg.ng/?MerchantCode=QWxhZGRpbjpvcGVuc2VzYW1l
      if (queryCode && queryHash) {
        const sanitized = sanitizeCredentials(queryCode, queryHash);
        if (sanitized) {
          validateUser(sanitized);
          return;
        }
      }

      const localCode = localStorage.getItem("dxz");
      const localHash = localStorage.getItem("xdg");
      // options 3: local storage injection //https://widget.vgg.ng/

      if (localCode && localHash) {
        const sanitized = sanitizeCredentials(localCode, localHash);
        if (sanitized) {
          validateUser(sanitized);

          const customerName = localStorage.getItem("CustomerName");
          const customerId = localStorage.getItem("CustomerId");

          if (customerName || customerId) {
            localStorage.setItem(
              "trips_user",
              JSON.stringify({
                CustomerName: customerName || "",
                CustomerId: customerId || "",
              })
            );
          }
          return;
        }
      }
      // option 4: redirect to  existing user already logged in in session //https://widget.vgg.ng/trips/home
      const existingAuth = localStorage.getItem("trips_auth");
      if (existingAuth) {
        const handleExistingAuth = async () => {
          try {
            const authObj = JSON.parse(existingAuth);
            
            const roadVisible = authObj?.ExtraData?.TransportMode?.find(
              (item: any) => item.TransportModeName === 'Road'
            )?.Status;

            if (roadVisible) {
              const storedRoutes = localStorage.getItem('roadRoutes');
              if (storedRoutes) {
                setData(JSON.parse(storedRoutes), 'roadRoutes');
              } else {
                setLoading(true);
                const roadRoutesAPI = await getAPI(GET_SUPPORTED_ROAD_ROUTES);
                setLoading(false);
                if (roadRoutesAPI.status === 200 && roadRoutesAPI.data?.Data) {
                  setData(roadRoutesAPI.data.Data, 'roadRoutes');
                  localStorage.setItem('roadRoutes', JSON.stringify(roadRoutesAPI.data.Data));
                }
              }
            }

            const searchPayload = urlSearchParams.get("searchPayload");
            const userParamRaw = urlSearchParams.get("user");
            if (userParamRaw) {
              processSSOUser(userParamRaw);
            }
            if (searchPayload) {
              const handled = await processSearchPayload(searchPayload, navigate, setData);
              if (handled) return;
            }

            if (isEmbedded()) {
              redirectHome();
              return;
            }
            navigate("/home");
          } catch (parseError) {
            localStorage.removeItem("trips_auth");
            console.warn("Corrupted auth data cleared");
          }
        };
        handleExistingAuth();
        return;
      }

      setError("You need authorization to access this app. Please ensure you have valid credentials.");
    } catch (err) {
      console.error("Error processing authentication:", err);
      setError("Failed to load widget. Please refresh or contact support.");
    }
  }, [paramCode, paramHash, validateUser, navigate]);

  useEffect(() => {
    if (loading) {
      setError("");
    }
  }, [loading]);

  return (
    <Wrapper>
      <Spin spinning={loading} tip="Loading content..." />
      {error && !loading && (
        <Empty
          description={
            <div>
              <StyledTitle level={4}>{error}</StyledTitle>
              {error.includes("authorization") && (
                <p style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
                  Please contact your system administrator for access credentials.
                </p>
              )}
            </div>
          }
        />
      )}
    </Wrapper>
  );
}

export default Index;
